/**
 * VenuePendingApproval — Block-screen shown to venue_owner users whose
 * admin review has not resolved yet. Mirrors the vendor PendingApproval
 * card but with venue-specific copy. Status variants:
 *   • awaiting_assignment — no venue row yet (admin hasn't created one)
 *   • pending             — venue exists but admin hasn't approved
 *   • rejected            — admin rejected the venue
 */
import React from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import SEOHead from '@components/common/SEOHead';

const STATUS_CONFIG = {
  pending: {
    icon: 'bi-hourglass-split',
    iconColor: '#F59E0B',
    badge: { bg: 'warning', text: 'dark', label: 'Pending Approval' },
    title: 'Your venue is under review',
    message: 'Our team is reviewing your venue listing and will activate your account shortly. You\'ll receive an email as soon as you\'re approved — typically within 1 business day.',
  },
  awaiting_assignment: {
    icon: 'bi-building-add',
    iconColor: '#7C3AED',
    badge: { bg: 'info', text: 'light', label: 'Setup In Progress' },
    title: 'Your venue listing is being set up',
    message: 'Welcome to VowsEra! Our team is preparing your venue listing. We\'ll send you an email once everything is ready and your portal is unlocked — usually within 1 business day.',
  },
  rejected: {
    icon: 'bi-x-octagon',
    iconColor: '#DC2626',
    badge: { bg: 'danger', text: 'light', label: 'Application Declined' },
    title: 'Your venue application was not approved',
    message: 'Unfortunately, your venue application did not meet our current requirements. If you believe this was a mistake or you\'d like to discuss it further, please reach out to our support team.',
  },
};

const VenuePendingApproval = ({ status = 'pending', venue = null }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  return (
    <>
      <SEOHead title="Pending Approval" noIndex />
      <div className="d-flex justify-content-center align-items-start py-5 px-3">
        <Card className="border-0 shadow-sm" style={{ maxWidth: 560, width: '100%', borderRadius: 16 }}>
          <Card.Body className="p-4 p-md-5 text-center">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 72, height: 72, borderRadius: '50%', background: `${cfg.iconColor}1A` }}
            >
              <i className={`bi ${cfg.icon}`} style={{ color: cfg.iconColor, fontSize: 32 }} />
            </div>
            <Badge bg={cfg.badge.bg} text={cfg.badge.text} pill className="mb-3 px-3 py-2">
              {cfg.badge.label}
            </Badge>
            <h4 className="fw-bold mb-2" style={{ color: '#1D1D44' }}>{cfg.title}</h4>
            <p className="text-muted mb-4">{cfg.message}</p>

            {venue && (
              <div className="text-start bg-light rounded p-3 mb-4">
                <div className="small text-muted text-uppercase mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                  Submitted Details
                </div>
                {venue.name && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted small">Venue</span>
                    <span className="fw-semibold small">{venue.name}</span>
                  </div>
                )}
                {venue.city && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted small">Location</span>
                    <span className="fw-semibold small">{venue.city}{venue.state ? `, ${venue.state}` : ''}</span>
                  </div>
                )}
                {venue.capacity && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted small">Capacity</span>
                    <span className="fw-semibold small">{venue.capacity} guests</span>
                  </div>
                )}
              </div>
            )}

            <Button variant="outline-secondary" href="mailto:support@vowsera.com">
              <i className="bi bi-envelope me-1" />
              Contact Support
            </Button>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default VenuePendingApproval;
